import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apicallApi } from '../../../api/apicall';
import Navbar from '../../layout/Navbar';
import { Search, Plus, Trash2, Edit } from 'lucide-react';

function ApiCallsList() {
  const [apiCalls, setApiCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchApiCalls();
  }, []);

  const fetchApiCalls = async () => {
    try {
      setLoading(true);
      const data = await apicallApi.getAllApiCalls();
      setApiCalls(data);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to fetch API calls');
      setLoading(false);
    }
  };

  const handleDeleteApiCall = async (id) => {
    if (window.confirm('Are you sure you want to delete this API call?')) {
      try {
        await apicallApi.deleteApiCall(id);
        setApiCalls(apiCalls.filter(call => call.id !== id));
      } catch (err) {
        setError(err.message || 'Failed to delete API call');
      }
    }
  };

  const filteredApiCalls = apiCalls.filter(call => {
    // Filter by search term
    return call.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      call.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
      call.method.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Get method color
  const getMethodColor = (method) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'bg-blue-100 text-blue-800';
      case 'POST': return 'bg-green-100 text-green-800';
      case 'PUT': return 'bg-yellow-100 text-yellow-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      case 'PATCH': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">API Calls</h1>
          <Link
            to="/api-calls/create"
            className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          >
            <Plus size={16} />
            New API Call
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder="Search API calls..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:border-black"
              />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-2 text-gray-600">Loading API calls...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg">
              <p>{error}</p>
            </div>
          ) : filteredApiCalls.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No API calls found</p>
              {searchTerm ? (
                <p className="text-gray-400 mt-2">Try adjusting your search filters</p>
              ) : (
                <Link to="/api-calls/create" className="text-blue-500 hover:underline mt-2 inline-block">
                  Create your first API call
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">URL Template</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Position</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredApiCalls.map(call => (
                    <tr key={call.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getMethodColor(call.method)}`}>
                          {call.method}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{call.name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {call.url}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-500">
                          {call.position}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="flex justify-end items-center space-x-2">
                          <Link
                            to={`/api-calls/edit/${call.id}`}
                            className="p-1.5 text-blue-600 hover:text-blue-800 transition-colors"
                            title="Edit API call"
                          >
                            <Edit size={16} />
                          </Link>
                          <button
                            onClick={() => handleDeleteApiCall(call.id)}
                            className="p-1.5 text-red-600 hover:text-red-800 transition-colors"
                            title="Delete API call"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ApiCallsList; 